import React, { Fragment } from "react";
import { ChangeEvent } from "react";

import { DateTime } from "luxon";

import { IAuthProps } from "../AuthContext";
import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatUSD, formatDateLocal } from "../../Utilities";
import withAuth from "../withAuth";
import withTitle from "../withTitle";


interface IMetricsStatementsViewState {
    isLoading: boolean | string;
    hasChanged: boolean;
    searchStart: string;
    searchEnd: string;
    searchMonth: string;
    dateType: "month" | "range";

    metrics: any[];

    [name: string]: any;
}


class MetricsStatementsView extends React.Component<IAuthProps, IMetricsStatementsViewState> {
    constructor(props: Readonly<IAuthProps>) {
        super(props);

        const today = DateTime.local();

        this.state = {
            isLoading: true,
            hasChanged: false,
            searchStart: today.startOf("month").toISODate(),
            searchEnd: today.endOf("month").toISODate(),
            searchMonth: today.startOf("month").toFormat("yyyy-MM"),
            dateType: "month",
            dataMode: "appointments",

            metrics: [],
        };

        this.search = this.search.bind(this);
        this.handleElementChange = this.handleElementChange.bind(this);
    }

    public componentDidMount() {
        this.search();
    }

    public componentDidUpdate(prevProps: Readonly<IAuthProps>, prevState: Readonly<IMetricsStatementsViewState>) {
        if (this.state.searchStart !== prevState.searchStart && !this.state.searchStart) {
            // Default back to safe value
            this.setState({
                searchStart: DateTime.local().startOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchEnd !== prevState.searchEnd && !this.state.searchEnd) {
            // Default back to safe value
            this.setState({
                searchEnd: DateTime.local().endOf("month").toISODate()
            }, () => this.search());
        }
        else if (this.state.searchMonth !== prevState.searchMonth && !this.state.searchMonth) {
            // Default back to safe value
            this.setState({
                searchMonth: DateTime.local().startOf("month").toFormat("yyyy-MM")
            }, () => this.search());
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <div className="filter-control-box">
                    <select name="dateType" value={this.state.dateType} onChange={this.handleElementChange}>
                        <option value="month">Month</option>
                        <option value="range">Date Range</option>
                    </select>
                    {
                        this.state.dateType === "range"
                            ? <Fragment>
                                <div className="control-group fill-space">
                                    <span>Start Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                                </div>
                                <div className="control-group fill-space">
                                    <span>End Date:</span>
                                    <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                                </div>
                            </Fragment>
                            : <DelayInput type="month" className="fill-space" timeout={500} value={this.state.searchMonth} name="searchMonth" onChangeDelayed={this.handleElementChange} />
                    }
                </div>

                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th className="col-smallest">Posted</th>
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Statement Metrics</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            this.state.isLoading
                                ? <tr>
                                    <td colSpan={11}>
                                        {
                                            typeof this.state.isLoading === "string"
                                                ? <span className="error-text">{this.state.isLoading}</span>
                                                : <div className="spinner-center lds-dual-ring" />
                                        }
                                    </td>
                                </tr>
                                : this.state.metrics.length === 0 
                                    ? <tr>
                                        <td colSpan={11}>No payments found</td>
                                    </tr>
                                    :this.state.metrics.map(a =>
                                    <tr key={a.date}>
                                        <td data-caption="Name: ">{formatDateLocal(a.date)}</td>
                                        <td className="col-right-vt" data-caption="Posted: ">{formatUSD(a.total)}</td>
                                    </tr>)
                        }
                    </tbody>
                </table>
            </div >
        );
    }

    private async search() {
        try {
            if (!this.state.searchStart || !this.state.searchEnd) {
                this.setState({
                    employees: [],
                    completionStats: [],
                    isLoading: false,
                });
                return;
            }

            this.setState({
                isLoading: true
            });

            let after;
            let before;
            if (this.state.dateType === "month") {
                after = DateTime.fromISO(this.state.searchMonth).startOf("month");
                before = after.endOf("month");
            }
            else {
                after = DateTime.fromISO(this.state.searchStart).startOf("day");
                before = DateTime.fromISO(this.state.searchEnd).endOf("day");
            }

            const parameters = [
                `after=${after.toISO()}`,
                `before=${before.toISO()}`
            ];

            const data = await V2.get<any[]>(`statements/metrics?${parameters.join("&")}`);

            // data.sort((a, b) => (b.isMarketer - a.isMarketer) || formatName(a.employee.name).localeCompare(formatName(b.employee.name)));

            this.setState({
                metrics: data,
                isLoading: false,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        }, () => this.search());
    }
}

export default withTitle("Statement Metrics", withAuth(MetricsStatementsView));
